<script setup lang="ts">
import { computed } from 'vue'

import {
  useEventSettings,
  useSwitchTeamColors,
} from '@desktop/views/Relation/composables'
import { useBlockades } from '@desktop/views/Relation/Sports/composables'

interface Props {
  possessionHomeSrc: string
  possessionAwaySrc: string
}

const props = defineProps<Props>()

// composables
const { eventSettings } = useEventSettings()
const { getTeamSideColor, isSwitchedTeamColors } = useSwitchTeamColors()
const { isPitchBlocked } = useBlockades()

// computed
const teamSideColor = computed(() => {
  if (!eventSettings.value.teamSidePossession) {
    return null
  }

  return getTeamSideColor(eventSettings.value.teamSidePossession)
})

const indicatorSrc = computed(() => {
  if (!teamSideColor.value) {
    return null
  }

  return teamSideColor.value === 'home'
    ? props.possessionHomeSrc
    : props.possessionAwaySrc
})

const indicatorClass = computed(() => {
  const { teamSidePossession, teamSideChanged } = eventSettings.value
  if (!teamSidePossession) return ''

  const sideChanged = teamSideChanged === true ? 'true' : 'false'

  type PositionRotation = { position: string; rotation: string }
  type TeamInPossessionClass = {
    true: PositionRotation
    false: PositionRotation
  }

  const teamClasses: {
    home: TeamInPossessionClass
    away: TeamInPossessionClass
  } = {
    home: {
      true: {
        position: 'right-0',
        rotation: isSwitchedTeamColors.value ? '' : 'rotate-180',
      },
      false: {
        position: 'left-0',
        rotation: isSwitchedTeamColors.value ? 'rotate-180' : '',
      },
    },
    away: {
      true: {
        position: 'left-0',
        rotation: isSwitchedTeamColors.value ? '' : 'rotate-180',
      },
      false: {
        position: 'right-0',
        rotation: isSwitchedTeamColors.value ? 'rotate-180' : '',
      },
    },
  }

  const { position, rotation } = teamClasses[teamSidePossession][sideChanged]

  return `${position} ${rotation}`
})
</script>

<template>
  <img
    v-if="!isPitchBlocked() && indicatorSrc"
    class="pointer-events-none absolute h-full select-none"
    :class="indicatorClass"
    :src="indicatorSrc"
    alt="Possession indicator"
  />
</template>
